import React, { Fragment, type JSX } from 'react';

import { useIntl } from 'react-intl';

import { AVATAR_SIZES } from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { ModalBody, ModalHeader } from '@atlaskit/modal-dialog';
import { Box, Flex, Grid, Stack } from '@atlaskit/primitives/compiled';
import { AGENT_AVATAR_CLIP_PATH } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import messages from '../messages.ts';

const LINE = 20;
const HEADING = 32;
const SUBLINE = 20;
const SECTION = 20;
const SECTION_LABEL = 12;
const BEHAVIOUR_LINE = 20;
const CTA_START_SESSION = 140;
const CTA_CHAT = 180;
const CTA_MENU = 32;
const CTA_HEIGHT = 36;

const styles = cssMap({
	placeholderContainer: {
		width: '100%',
	},
	chatRow: {
		width: '100%',
	},
	titleAndStarRow: {
		width: '100%',
	},
	modalHeaderBackTitle: {
		width: '100%',
	},
	modalHeaderBackIcon: {
		flexShrink: 0,
	},
	modalHeaderPageTitleTrack: {
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: '14rem',
		minWidth: '12rem',
		maxWidth: '20rem',
	},
	actionButtonRow: {
		width: '100%',
	},
	modalBody: {
		paddingBottom: token('space.300'),
		height: '100%',
		gridTemplateColumns: '1fr 2fr',
	},
	banner: {
		marginBottom: token('space.500'),
		marginInline: token('space.negative.400'),
	},
	avatar: {
		marginBottom: token('space.300'),
	},
	hexAvatarSkeleton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	aboutCard: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		marginTop: token('space.300'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.raised'),
	},
	sectionContentBox: {
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	rightColumn: {
		minWidth: '0px',
		width: '100%',
	},
});

export type ViewAgentDetailSkeletonProps = {
	/** Root `data-testid` for tests (default: view-agent-placeholder). */
	testId?: string;
};

/**
 * Layout skeleton for the agent detail view in the browse-agents modal while
 * agent data and Relay data are loading. Mirrors the two-column view structure
 * and uses the same hex clip path and `xxlarge` size as `AgentAvatar` in the loaded view.
 */
export const ViewAgentDetailSkeleton = ({
	testId = 'view-agent-placeholder',
}: ViewAgentDetailSkeletonProps = {}): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box aria-busy="true" xcss={styles.placeholderContainer} testId={testId}>
			<VisuallyHidden role="status">{formatMessage(messages.loadingAgentDetails)}</VisuallyHidden>
			<Fragment>
				{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
				<ModalHeader hasCloseButton>
					<Flex
						direction="row"
						alignItems="center"
						gap="space.100"
						wrap="nowrap"
						xcss={styles.modalHeaderBackTitle}
					>
						<Box xcss={styles.modalHeaderBackIcon}>
							<Skeleton
								testId="modal-skeleton-1"
								height={32}
								width={32}
								isShimmering
								borderRadius={token('radius.small')}
							/>
						</Box>
						<Box xcss={styles.modalHeaderPageTitleTrack}>
							<Skeleton
								testId="modal-skeleton-2"
								height={24}
								width="100%"
								isShimmering
								borderRadius={token('radius.small')}
							/>
						</Box>
					</Flex>
				</ModalHeader>
				<ModalBody>
					<Box xcss={styles.banner}>
						<Skeleton height={96} width="100%" isShimmering borderRadius={token('radius.small')} />
					</Box>
					<Grid xcss={styles.modalBody} columnGap="space.300">
						<Stack space="space.200" alignBlock="start">
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- mirrors loaded view `view-agent/index` avatar overlap */}
							<Box xcss={styles.avatar} style={{ marginTop: '-105px' }}>
								<Box
									xcss={styles.hexAvatarSkeleton}
									style={{
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
										width: AVATAR_SIZES.xxlarge,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
										height: AVATAR_SIZES.xxlarge,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
										clipPath: AGENT_AVATAR_CLIP_PATH,
									}}
								>
									<Skeleton
										testId="modal-skeleton-3"
										width="100%"
										height="100%"
										isShimmering
										borderRadius={0}
									/>
								</Box>
							</Box>
							<Flex
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								wrap="nowrap"
								xcss={styles.titleAndStarRow}
							>
								<Skeleton
									height={HEADING}
									width="min(100%, 18rem)"
									isShimmering
									borderRadius={token('radius.small')}
								/>
								<Skeleton
									height={32}
									width={32}
									isShimmering
									borderRadius={token('radius.small')}
								/>
							</Flex>
							{/* creator line */}
							<Skeleton
								height={SUBLINE}
								width="min(100%, 14rem)"
								isShimmering
								borderRadius={token('radius.small')}
							/>
							{/* description (`AgentProfileInfo` <p> when set) — two text lines, not one slab */}
							<Stack space="space.050">
								<Skeleton
									height={LINE}
									width="100%"
									isShimmering
									borderRadius={token('radius.small')}
								/>
								<Skeleton
									height={LINE}
									width="90%"
									isShimmering
									borderRadius={token('radius.small')}
								/>
							</Stack>
							{/* favourite / “N users” row */}
							<Skeleton
								height={LINE}
								width="7rem"
								isShimmering
								borderRadius={token('radius.small')}
							/>
							<Flex
								direction="row"
								alignItems="center"
								gap="space.100"
								wrap="nowrap"
								xcss={styles.actionButtonRow}
							>
								<Skeleton
									height={CTA_HEIGHT}
									width={CTA_START_SESSION}
									isShimmering
									borderRadius={token('radius.small')}
								/>
								<Skeleton
									height={CTA_HEIGHT}
									width={CTA_CHAT}
									isShimmering
									borderRadius={token('radius.small')}
								/>
								<Skeleton
									height={CTA_MENU}
									width={CTA_MENU}
									isShimmering
									borderRadius={token('radius.small')}
								/>
							</Flex>
							{/* `AgentAboutWrapper` + `InfoSection` x4: uppercase label, then one content row each */}
							<Box xcss={styles.aboutCard}>
								<Stack space="space.300" alignBlock="start">
									<Stack space="space.100" alignBlock="start">
										<Skeleton
											height={SECTION_LABEL}
											width={64}
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Flex direction="row" alignItems="center" gap="space.100" xcss={styles.chatRow}>
											<Skeleton
												height={20}
												width={20}
												isShimmering
												borderRadius={token('radius.small')}
											/>
											<Skeleton
												height={LINE}
												width="12rem"
												isShimmering
												borderRadius={token('radius.small')}
											/>
										</Flex>
									</Stack>
									<Stack space="space.100" alignBlock="start">
										<Skeleton
											height={SECTION_LABEL}
											width="6.5rem"
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="100%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Stack>
									<Stack space="space.100" alignBlock="start">
										<Skeleton
											height={SECTION_LABEL}
											width="4rem"
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="100%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Stack>
									<Stack space="space.100" alignBlock="start">
										<Skeleton
											height={SECTION_LABEL}
											width="4.5rem"
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="100%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Stack>
								</Stack>
							</Box>
						</Stack>
						<Stack space="space.400" alignBlock="start" xcss={styles.rightColumn}>
							{/* RightSection: heading + conversation starters (icon + line per item) */}
							<Stack space="space.200" alignBlock="start">
								<Skeleton
									height={SECTION}
									width="6.25rem"
									isShimmering
									borderRadius={token('radius.small')}
								/>
								<Stack space="space.150" alignBlock="start">
									<Flex direction="row" alignItems="center" gap="space.100" xcss={styles.chatRow}>
										<Skeleton
											height={20}
											width={20}
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="100%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Flex>
									<Flex direction="row" alignItems="center" gap="space.100" xcss={styles.chatRow}>
										<Skeleton
											height={20}
											width={20}
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="100%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Flex>
									<Flex direction="row" alignItems="center" gap="space.100" xcss={styles.chatRow}>
										<Skeleton
											height={20}
											width={20}
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="92%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Flex>
								</Stack>
							</Stack>
							<Stack space="space.200" alignBlock="start">
								<Skeleton
									height={SECTION}
									width="5.5rem"
									isShimmering
									borderRadius={token('radius.small')}
								/>
								<Box xcss={styles.sectionContentBox}>
									<Skeleton
										height={BEHAVIOUR_LINE}
										width="100%"
										isShimmering
										borderRadius={token('radius.small')}
									/>
								</Box>
							</Stack>
							<Stack space="space.200" alignBlock="start">
								<Skeleton
									height={SECTION}
									width="6.5rem"
									isShimmering
									borderRadius={token('radius.small')}
								/>
								<Box xcss={styles.sectionContentBox}>
									<Stack space="space.075" alignBlock="start">
										<Skeleton
											height={LINE}
											width="100%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="98%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="100%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="88%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
										<Skeleton
											height={LINE}
											width="72%"
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Stack>
								</Box>
							</Stack>
						</Stack>
					</Grid>
				</ModalBody>
			</Fragment>
		</Box>
	);
};
